'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Settings, Lightbulb, ShoppingCart, Users,
  CheckCircle, Sparkles, ChevronLeft, ChevronRight, ArrowUpRight,
} from 'lucide-react';

interface WorkCard {
  id: string; title: string; description: string; focusAreas: string[];
  icon: any; gradient: string; gradientFrom: string; gradientTo: string;
  tag: string; lightBg: string;
}

const workCards: WorkCard[] = [
  {
    id: '01', title: 'AI Automations',
    description: 'Operational inefficiencies create risk, cost, and lost momentum. We design intelligent automation systems that standardize execution, improve visibility, and enable scalable growth across marketing and E-commerce teams.',
    focusAreas: ['Structured automation mapping across core workflows', 'Unified workflow intelligence across marketing, sales & ops', 'Context-aware automations that respond to real-time signals', 'AI-assisted research, reporting and daily insights', 'Secure, no-code automation for daily tasks'],
    icon: Settings, gradient: 'from-primary to-pink-400', gradientFrom: '#fa00ac', gradientTo: '#ff6cd4', tag: 'Automation', lightBg: 'rgba(250,0,172,0.05)',
  },
  {
    id: '02', title: 'Intelligent Brand Building',
    description: 'We build brands through stories that earn trust and performance systems that drive results. Every message is shaped by data, refined by AI, and measured by real business impact.',
    focusAreas: ['Insight-led brand narratives built to convert', 'Performance-first creative testing and scale', 'Consistent storytelling across growth channels', 'AI-driven optimization for recall and ROI'],
    icon: Lightbulb, gradient: 'from-secondary to-blue-400', gradientFrom: '#274eff', gradientTo: '#6b8fff', tag: 'Branding', lightBg: 'rgba(39,78,255,0.05)',
  },
  {
    id: '03', title: 'E-Commerce That Scales',
    description: 'We design intelligent E-Commerce and customer lifecycle frameworks that turn traffic into revenue and customers into long-term value.',
    focusAreas: ['Conversion-first storefront and funnel optimization', 'AI-led personalization across the shopping journey', 'Lifecycle strategies for repeat purchase and retention', 'Performance visibility from first click for maximum revenue'],
    icon: ShoppingCart, gradient: 'from-success to-teal-400', gradientFrom: '#10b981', gradientTo: '#34d399', tag: 'E-Commerce', lightBg: 'rgba(16,185,129,0.05)',
  },
  {
    id: '04', title: 'Human-Led & System-Powered',
    description: 'AI moves fast — but growth still needs human judgment. With 16+ years of experience, our team actively monitors and optimizes supported by intelligent systems working round the clock.',
    focusAreas: ['Dedicated human oversight on every account', 'Proactive, real-time intervention', 'Continuous optimization, 24/7', 'Clear ownership and accountability'],
    icon: Users, gradient: 'from-primary via-secondary to-blue-400', gradientFrom: '#fa00ac', gradientTo: '#274eff', tag: 'Strategy', lightBg: 'rgba(250,0,172,0.04)',
  },
];

const GAP = 16;

function CardItem({ card }: { card: WorkCard }) {
  const Icon = card.icon;
  return (
    <div className="group relative rounded-[1.75rem] overflow-hidden cursor-default h-full">
      {/* Card base */}
      <div
        className="absolute inset-0 rounded-[1.75rem] border border-border/80 shadow-sm transition-shadow duration-500 group-hover:shadow-xl"
        style={{ background: `linear-gradient(145deg, #ffffff, ${card.lightBg})` }}
      />
      <div
        className="absolute inset-0 rounded-[1.75rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ boxShadow: `0 0 0 1.5px ${card.gradientFrom}40, 0 20px 60px -12px ${card.gradientFrom}30` }}
      />

      {/* ── Default face — sets the card height, arrow pinned to bottom ── */}
      <div className="relative z-10 flex flex-col p-5 min-h-[500px] h-full transition-all duration-500 group-hover:opacity-0 group-hover:scale-95 group-hover:pointer-events-none">
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border"
            style={{ color: card.gradientFrom, borderColor: `${card.gradientFrom}35`, background: `${card.gradientFrom}10` }}
          >
            {card.tag}
          </span>
          <span className="text-5xl font-black select-none leading-none" style={{ color: `${card.gradientFrom}12` }}>{card.id}</span>
        </div>

        <div className="relative flex items-center justify-center mb-4">
          <div className="absolute w-24 h-24 rounded-full blur-2xl opacity-20"
            style={{ background: `radial-gradient(circle, ${card.gradientFrom}, ${card.gradientTo})` }} />
          <div
            className="relative w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${card.gradientFrom}, ${card.gradientTo})`, boxShadow: `0 12px 40px -8px ${card.gradientFrom}66` }}
          >
            <Icon className="w-7 h-7 text-white" />
          </div>
        </div>

        <h3 className="text-base font-bold text-foreground mb-2 leading-snug">{card.title}</h3>
        {/* flex-1 pushes footer to bottom */}
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">{card.description}</p>

        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/60">
          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${card.gradientFrom}15` }}>
            <ArrowUpRight className="w-3.5 h-3.5" style={{ color: card.gradientFrom }} />
          </div>
        </div>
      </div>

      {/* ── Hover face — absolute, slides in over default face ── */}
      <div className="absolute inset-0 z-20 flex flex-col p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
        <div className="absolute inset-0" style={{ background: `linear-gradient(145deg, ${card.gradientFrom}f0, ${card.gradientTo}e0)` }} />
        <div className="absolute inset-0 bg-white/10" />
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-black/10 blur-2xl pointer-events-none" />

        <div className="relative flex flex-col h-full">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-white/25 backdrop-blur-sm">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[0.6rem] text-white/60 uppercase tracking-widest font-bold">{card.tag}</p>
                <h3 className="text-sm font-bold text-white leading-tight">{card.title}</h3>
              </div>
            </div>
            <span className="text-3xl font-black text-white/10 select-none">{card.id}</span>
          </div>

          <div className="w-full h-px bg-white/20 mb-2 flex-shrink-0" />
          <p className="text-[0.6rem] font-black uppercase tracking-[0.22em] text-white/60 mb-2 flex-shrink-0">Strategic Focus Areas</p>

          <ul className="flex flex-col gap-1.5">
            {card.focusAreas.map((area, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-2.5 rounded-xl bg-white/15 border border-white/20 transition-all duration-200 hover:bg-white/25 hover:translate-x-1"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <div className="w-5 h-5 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs text-white/90 leading-snug font-medium">{area}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function HowWeWorkSection() {
  const trackRef    = useRef<HTMLDivElement>(null);
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const cardWidthRef = useRef(0);
  const activeIdxRef = useRef(0);
  const isTouching  = useRef(false);
  const touchStartX = useRef(0);
  const mobileRef   = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth]     = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!mobileRef.current) return;
      const w = mobileRef.current.clientWidth;
      const cw = w - 32;
      setCardWidth(cw);
      cardWidthRef.current = cw;
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (mobileRef.current) ro.observe(mobileRef.current);
    return () => ro.disconnect();
  }, []);

  const scrollToCard = (index: number) => {
    const track = trackRef.current;
    const w = cardWidthRef.current;
    if (!track || w === 0) return;
    track.scrollTo({ left: index * (w + GAP), behavior: 'smooth' });
  };

  const startAutoPlay = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (isTouching.current) return;
      const next = (activeIdxRef.current + 1) % workCards.length;
      activeIdxRef.current = next;
      setActiveIndex(next);
      scrollToCard(next);
    }, 3000);
  };

  useEffect(() => {
    if (cardWidth > 0) startAutoPlay();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardWidth]);

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(workCards.length - 1, index));
    activeIdxRef.current = clamped;
    setActiveIndex(clamped);
    scrollToCard(clamped);
    startAutoPlay();
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const w = cardWidthRef.current;
      if (w === 0) return;
      const idx = Math.min(workCards.length - 1, Math.round(track.scrollLeft / (w + GAP)));
      if (idx !== activeIdxRef.current) {
        activeIdxRef.current = idx;
        setActiveIndex(idx);
      }
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    isTouching.current = true;
    touchStartX.current = e.touches[0].clientX;
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      const next = diff > 0
        ? Math.min(workCards.length - 1, activeIdxRef.current + 1)
        : Math.max(0, activeIdxRef.current - 1);
      goTo(next);
    }
    setTimeout(() => {
      isTouching.current = false;
      startAutoPlay();
    }, 1500);
  };

  // Shared section header
  const SectionHeader = ({ showControls }: { showControls: boolean }) => (
    <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border shadow-sm mb-4">
          <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-widest">Our Approach</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
          How AI Fits In{' '}
          <span className="relative inline-block">
            <span className="relative z-10">(Without the Hype)</span>
            <span className="absolute bottom-1 left-0 w-full h-3 bg-accent/40 -rotate-1 rounded-sm" />
          </span>
        </h2>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl">
          Intelligent automation meets human expertise — built to drive real, measurable growth.
        </p>
      </div>

      {showControls && (
        <div className="flex-shrink-0 flex flex-col items-end gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => goTo((activeIndex - 1 + workCards.length) % workCards.length)}
              aria-label="Previous"
              className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center shadow-sm transition-all duration-200 hover:border-primary/40 hover:shadow-md hover:scale-105"
            >
              <ChevronLeft className="w-4 h-4 text-foreground" />
            </button>
            <button
              onClick={() => goTo((activeIndex + 1) % workCards.length)}
              aria-label="Next"
              className="w-10 h-10 rounded-full border border-border bg-card flex items-center justify-center shadow-sm transition-all duration-200 hover:border-primary/40 hover:shadow-md hover:scale-105"
            >
              <ChevronRight className="w-4 h-4 text-foreground" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            {workCards.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 ${i === activeIndex ? 'w-6 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-border hover:bg-muted-foreground/40'}`}
                aria-label={`Go to card ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section
      id="how-we-work"
      className="relative py-10 md:py-14 lg:py-16 bg-gradient-to-br from-pink-50 via-slate-50 to-blue-50 overflow-hidden"
    >
      {/* ── Ambient background ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_20%,rgba(250,0,172,0.16),transparent_42%),radial-gradient(circle_at_82%_18%,rgba(39,78,255,0.15),transparent_42%),radial-gradient(circle_at_50%_86%,rgba(16,185,129,0.13),transparent_44%)]" />
        <div className="absolute -top-24 -left-20 w-[26rem] h-[26rem] rounded-full bg-pink-400/20 blur-3xl animate-float-soft" />
        <div className="absolute top-1/4 -right-20 w-[30rem] h-[30rem] rounded-full bg-blue-400/16 blur-3xl animate-float" />
        <div className="absolute -bottom-24 left-1/3 w-[26rem] h-[26rem] rounded-full bg-emerald-400/14 blur-3xl animate-float-soft" style={{ animationDelay: '700ms' }} />
      </div>

      {/* ── MOBILE: carousel (hidden on md+) ── */}
      <div ref={mobileRef} className="md:hidden w-full px-4 relative z-10">
        <SectionHeader showControls={true} />
        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden pb-1"
          style={{ scrollbarWidth: 'none' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {workCards.map((card) => (
            <div
              key={card.id}
              className="flex-shrink-0 snap-start"
              style={{ width: cardWidth > 0 ? cardWidth : '85%' }}
            >
              <CardItem card={card} />
            </div>
          ))}
        </div>
      </div>

      {/* ── DESKTOP: full grid (hidden below md) ── */}
      <div className="hidden md:block w-full px-6 lg:px-10 xl:px-16 relative z-10">
        <SectionHeader showControls={false} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 items-stretch">
          {workCards.map((card) => (
            <CardItem key={card.id} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
}
